// ─────────────────────────────────────────────────────────────────────────────
// data/portfolioData.js  –  Single source of truth
// ─────────────────────────────────────────────────────────────────────────────

export const personal = {
  name: "Shakeel Khan",
  title: "Blockchain & Full Stack Developer",
  bio: "Hi, I'm Shakeel — I build AI-powered platforms, decentralised smart-contract systems, and high-throughput backends. 4+ years in blockchain engineering across DeFi, NFTs, AI infrastructure, and distributed computing.",
  location: "Karachi, Pakistan",
  remote: true,
  email: "shakeelkhanaus@gmail.com",
  github: "https://github.com/Shinwari536",
  githubHandle: "Shinwari536",
  linkedin: "https://www.linkedin.com/in/shakeel-khan-547a961a3/",
  linkedinHandle: "shakeel-khan-547a961a3",
  available: true,
};

// Derived from actual work history:
// - Blockchain: TechThree May 2023 – present + Chainz May 2022 – May 2023 = 3+ years deep blockchain
// - Smart contracts shipped: 8+ at Chainz (TOY NFT, DEDW, Capped Ranged, game market + 4 from TechThree) → 10+
// - Total professional projects: 6 (TechThree) + 4 (Chainz) + 13 Android + RB SOL projects = 25+
// - Android apps: confirmed 13+
export const stats = [
  { num: "3+",  label: "Years in Blockchain" },
  { num: "10+", label: "Smart Contracts Shipped" },
  { num: "5+",  label: "Production DApps" },
];

export const aboutChips = [
  "Solidity", "Node.js", "EVM / Web3", "RAG / LLMs",
  "Smart Contracts", "PostgreSQL", "AWS", "Post-Quantum Crypto",
  "EIP-712", "DeFi / NFT",
];

// ─── Skills ───────────────────────────────────────────────────────────────────
export const skillCategories = [
  { id: "all",        label: "All",        icon: "✦",  centerSub: "Full Stack" },
  { id: "blockchain", label: "Blockchain", icon: "⛓",  centerSub: "Web3 Core" },
  { id: "backend",    label: "Backend",    icon: "⚙",  centerSub: "Server Side" },
  { id: "ai",         label: "AI & Data",  icon: "🧠", centerSub: "Intelligence" },
  { id: "frontend",   label: "Frontend",   icon: "⚛",  centerSub: "UI Layer" },
  { id: "tools",      label: "DevOps",     icon: "🛠", centerSub: "Infrastructure" },
];

// logo = SVG path data or a well-known letter/symbol — used for filled circle
export const skills = [
  // ── Blockchain – outer ring (r≈210)
  { id:"solidity",   cat:"blockchain", label:"Solidity",      desc:"Smart-contract language",        logo:"SOL",  logoType:"text", bg:"#627eea", ang:0,   r:210 },
  { id:"evm",        cat:"blockchain", label:"EVM / Web3",    desc:"Ethereum Virtual Machine",       logo:"⧫",    logoType:"emoji",bg:"#f6851b", ang:45,  r:210 },
  { id:"ethersjs",   cat:"blockchain", label:"Ethers.js",     desc:"Ethereum JS library",            logo:"ETH",  logoType:"text", bg:"#6b46c1", ang:90,  r:210 },
  { id:"hardhat",    cat:"blockchain", label:"Hardhat",       desc:"EVM dev environment",            logo:"🔨",   logoType:"emoji",bg:"#fbbf24", ang:135, r:210 },
  { id:"defi",       cat:"blockchain", label:"DeFi",          desc:"Uniswap V2/V3, AMM, DEX",        logo:"💱",   logoType:"emoji",bg:"#10b981", ang:180, r:210 },
  { id:"ipfs",       cat:"blockchain", label:"IPFS",          desc:"Decentralised storage",          logo:"🌐",   logoType:"emoji",bg:"#06b6d4", ang:225, r:210 },
  { id:"erc",        cat:"blockchain", label:"ERC Standards", desc:"ERC-20/721/721A/1155/2981",      logo:"ERC",  logoType:"text", bg:"#8b5cf6", ang:270, r:210 },
  { id:"pqc",        cat:"blockchain", label:"Post-Quantum",  desc:"NIST-aligned crypto primitives", logo:"🔐",   logoType:"emoji",bg:"#E84393", ang:315, r:210 },

  // ── Backend – inner ring (r≈128)
  { id:"nodejs",     cat:"backend", label:"Node.js",    desc:"Server-side JS runtime",      logo:"N",    logoType:"text", bg:"#68a063", ang:0,   r:128 },
  { id:"postgresql", cat:"backend", label:"PostgreSQL", desc:"Relational database",         logo:"PG",   logoType:"text", bg:"#336791", ang:60,  r:128 },
  { id:"restws",     cat:"backend", label:"REST / WS",  desc:"APIs & WebSockets",           logo:"⚡",   logoType:"emoji",bg:"#f59e0b", ang:120, r:128 },
  { id:"python",     cat:"backend", label:"Python",     desc:"Scripting & AI pipelines",    logo:"Py",   logoType:"text", bg:"#3776ab", ang:180, r:128 },
  { id:"mongodb",    cat:"backend", label:"MongoDB",    desc:"Document-oriented database",  logo:"M",    logoType:"text", bg:"#4db33d", ang:240, r:128 },
  { id:"express",    cat:"backend", label:"Express.js", desc:"Minimal Node.js framework",   logo:"Ex",   logoType:"text", bg:"#6b7280", ang:300, r:128 },

  // ── AI / Data – outer ring (r≈200, offset angles)
  { id:"llm",        cat:"ai", label:"LLM APIs",     desc:"OpenAI, Anthropic & more",           logo:"AI",  logoType:"text", bg:"#7C3AED", ang:22,  r:200 },
  { id:"rag",        cat:"ai", label:"RAG",           desc:"Retrieval-Augmented Generation",     logo:"🧠",  logoType:"emoji",bg:"#E84393", ang:82,  r:200 },
  { id:"vectordb",   cat:"ai", label:"Vector DB",     desc:"Semantic & dense search",            logo:"V",   logoType:"text", bg:"#0EA5E9", ang:150, r:200 },
  { id:"nlp",        cat:"ai", label:"NLP",           desc:"Moderation, sentiment, FUD detect",  logo:"NLP", logoType:"text", bg:"#10b981", ang:210, r:200 },
  { id:"embeddings", cat:"ai", label:"Embeddings",    desc:"Dense vector representations",       logo:"📊",  logoType:"emoji",bg:"#f59e0b", ang:270, r:200 },
  { id:"aimod",      cat:"ai", label:"AI Moderation", desc:"Spam / phishing / FUD detection",    logo:"🛡",  logoType:"emoji",bg:"#ef4444", ang:335, r:200 },

  // ── Frontend – inner ring (r≈125)
  { id:"react",      cat:"frontend", label:"React",      desc:"UI component library",         logo:"⚛",  logoType:"emoji",bg:"#61dafb", ang:30,  r:125 },
  { id:"javascript", cat:"frontend", label:"JavaScript", desc:"Core web language",             logo:"JS", logoType:"text", bg:"#f7df1e", ang:120, r:125 },
  { id:"typescript", cat:"frontend", label:"TypeScript", desc:"Typed JavaScript",              logo:"TS", logoType:"text", bg:"#3178c6", ang:210, r:125 },
  { id:"tailwind",   cat:"frontend", label:"Tailwind",   desc:"Utility-first CSS framework",   logo:"TW", logoType:"text", bg:"#38bdf8", ang:300, r:125 },

  // ── Tools / DevOps – mixed rings
  { id:"aws",      cat:"tools", label:"AWS",          desc:"Cloud infrastructure",           logo:"AWS",  logoType:"text", bg:"#ff9900", ang:15,  r:205 },
  { id:"docker",   cat:"tools", label:"Docker",       desc:"Container platform",             logo:"🐳",   logoType:"emoji",bg:"#0db7ed", ang:75,  r:135 },
  { id:"github",   cat:"tools", label:"GitHub CI/CD", desc:"Actions, runners, automation",   logo:"GH",   logoType:"text", bg:"#333",    ang:155, r:205 },
  { id:"redis",    cat:"tools", label:"Redis",        desc:"In-memory data store",           logo:"R",    logoType:"text", bg:"#dc382d", ang:215, r:135 },
  { id:"nginx",    cat:"tools", label:"Nginx",        desc:"Web server / reverse proxy",     logo:"Nx",   logoType:"text", bg:"#009900", ang:285, r:205 },
  { id:"telegram", cat:"tools", label:"Telegram Bot", desc:"Bot API integrations",           logo:"✈",    logoType:"emoji",bg:"#229ED9", ang:345, r:135 },
];

// ─── Projects ─────────────────────────────────────────────────────────────────
// ORDER: GPU → Bot Moderation → Multisig Vault → Blockchain Analytics → Chainz projects
export const projects = [
  {
    id: "gpu-compute",
    title: "Decentralised GPU Compute Network",
    company: "TechThree",
    status: "Active Development",
    confidential: true,
    nda: true,
    category: "blockchain",
    accentColor: "#0EA5E9",
    desc: "Decentralised compute marketplace where GPU providers register nodes, receive jobs via a distributed scheduler, and earn on-chain rewards. Implements post-quantum encrypted communications and trustless billing.",
    highlights: [
      "GPU node registry & distributed job scheduler",
      "Post-quantum encrypted comms (NIST-aligned)",
      "EVM smart contracts for staking & token rewards",
      "Real-time telemetry & automated billing pipelines",
    ],
    tech: ["Node.js", "Solidity/EVM", "Post-Quantum Crypto", "Smart Contracts", "AWS", "PostgreSQL", "Tokenomics"],
    techColors: {
      "Node.js":            { bg:"rgba(104,160,99,0.15)",  color:"#68a063" },
      "Solidity/EVM":       { bg:"rgba(98,126,234,0.15)",  color:"#627eea" },
      "Post-Quantum Crypto":{ bg:"rgba(232,67,147,0.15)",  color:"#E84393" },
      "Smart Contracts":    { bg:"rgba(124,58,237,0.15)",  color:"#7C3AED" },
      "AWS":                { bg:"rgba(255,153,0,0.15)",   color:"#ff9900" },
      "PostgreSQL":         { bg:"rgba(51,103,145,0.15)",  color:"#336791" },
      "Tokenomics":         { bg:"rgba(246,133,27,0.15)",  color:"#f6851b" },
    },
    span: 2,
  },
  {
    id: "web3-ai-platform",
    title: "AI-Powered Web3 Community Automation",
    company: "TechThree",
    status: "Production",
    confidential: true,
    nda: true,
    category: "ai",
    accentColor: "#E84393",
    desc: "End-to-end platform combining NLP moderation, a RAG-based AI assistant, on-chain token gating, and real-time analytics for large Web3 communities. Handles spam, phishing, and FUD detection at scale.",
    highlights: [
      "NLP moderation pipeline — spam, phishing, FUD detection",
      "RAG AI assistant with hallucination reduction",
      "On-chain token gating via EVM / Web3",
      "Real-time community analytics dashboard",
    ],
    tech: ["Node.js", "PostgreSQL", "LLM APIs", "RAG", "EVM/Web3", "Telegram API", "Discord API", "AWS"],
    techColors: {
      "Node.js":     { bg:"rgba(104,160,99,0.15)",  color:"#68a063" },
      "PostgreSQL":  { bg:"rgba(51,103,145,0.15)",  color:"#336791" },
      "LLM APIs":    { bg:"rgba(124,58,237,0.15)",  color:"#7C3AED" },
      "RAG":         { bg:"rgba(232,67,147,0.15)",  color:"#E84393" },
      "EVM/Web3":    { bg:"rgba(246,133,27,0.15)",  color:"#f6851b" },
      "Telegram API":{ bg:"rgba(34,158,217,0.15)",  color:"#229ED9" },
      "Discord API": { bg:"rgba(88,101,242,0.15)",  color:"#5865F2" },
      "AWS":         { bg:"rgba(255,153,0,0.15)",   color:"#ff9900" },
    },
    span: 2,
  },
  {
    id: "multisig-vault",
    title: "Decentralised Multi-Signature Asset Vault",
    company: "TechThree",
    status: "Production",
    confidential: true,
    nda: true,
    category: "blockchain",
    accentColor: "#7C3AED",
    desc: "Secure, non-custodial multi-sig vault system for managing digital assets across distributed stakeholders. Requires M-of-N approvals before any transaction executes, eliminating single points of failure.",
    highlights: [
      "Configurable M-of-N signer sets with dynamic thresholds",
      "Full transaction lifecycle: propose → approve → execute → cancel",
      "EIP-712 off-chain signature aggregation for gas optimisation",
      "Replay-attack prevention, reentrancy guards, strict access control",
      "Off-chain coordination API with on-chain state synchronisation",
    ],
    tech: ["Solidity", "EIP-712", "Ethers.js", "Node.js", "PostgreSQL"],
    techColors: {
      "Solidity":   { bg:"rgba(98,126,234,0.15)",  color:"#627eea" },
      "EIP-712":    { bg:"rgba(124,58,237,0.15)",  color:"#7C3AED" },
      "Ethers.js":  { bg:"rgba(107,70,193,0.15)",  color:"#6b46c1" },
      "Node.js":    { bg:"rgba(104,160,99,0.15)",  color:"#68a063" },
      "PostgreSQL": { bg:"rgba(51,103,145,0.15)",  color:"#336791" },
    },
    span: 1,
  },
  {
    id: "blockchain-analytics",
    title: "Blockchain Intelligence & Analytics Platform",
    company: "TechThree",
    status: "Production",
    confidential: true,
    nda: true,
    category: "ai",
    accentColor: "#10b981",
    desc: "Transforms raw on-chain data into actionable insights. Classifies wallets by behaviour, tracks P&L, flags suspicious activity, and rewards users with a tokenised incentive mechanism.",
    highlights: [
      "Scalable pipeline ingesting token transfers, wallet activity, contract events",
      "Wallet intelligence: traders vs. holders vs. 'smart money' clustering",
      "Profit/loss tracking, entry/exit timing, holder distribution APIs",
      "Risk detection heuristics for suspicious tokens & abnormal patterns",
      "Tokenised reward system with nonce-based replay protection",
    ],
    tech: ["Node.js", "PostgreSQL", "EVM/Web3", "Custom Indexer", "Signature Validation"],
    techColors: {
      "Node.js":             { bg:"rgba(104,160,99,0.15)",  color:"#68a063" },
      "PostgreSQL":          { bg:"rgba(51,103,145,0.15)",  color:"#336791" },
      "EVM/Web3":            { bg:"rgba(246,133,27,0.15)",  color:"#f6851b" },
      "Custom Indexer":      { bg:"rgba(16,185,129,0.15)",  color:"#10b981" },
      "Signature Validation":{ bg:"rgba(232,67,147,0.15)",  color:"#E84393" },
    },
    span: 1,
  },
  {
    id: "toy-nft",
    title: "TOY NFT E-Commerce & Multi-Contract Platform",
    company: "Chainz Experts",
    status: "Production",
    confidential: false,
    nda: false,
    category: "blockchain",
    accentColor: "#f6851b",
    desc: "Full-stack NFT marketplace with two Solidity contracts (NFT + Escrow), Stripe fiat payments, and WebSocket event triggers. Followed by DEDW (ERC-2981 royalty), Capped Ranged (random minting, dual whitelist), and a blockchain game with an in-game NFT marketplace.",
    highlights: [
      "Multi-phase minting with whitelist & signature verification",
      "Escrow smart contract with automated Stripe webhook triggers",
      "DEDW — ERC-2981 royalty standard NFT collection",
      "Capped Ranged — random NFT mint with dual whitelist",
      "Blockchain game: weapon/armour NFTs + in-game marketplace",
    ],
    tech: ["Solidity", "ERC-721A", "ERC-1155", "ERC-2981", "Hardhat", "Node.js", "Stripe", "WebSocket"],
    techColors: {
      "Solidity":  { bg:"rgba(98,126,234,0.15)",  color:"#627eea" },
      "ERC-721A":  { bg:"rgba(124,58,237,0.15)",  color:"#7C3AED" },
      "ERC-1155":  { bg:"rgba(232,67,147,0.15)",  color:"#E84393" },
      "ERC-2981":  { bg:"rgba(245,158,11,0.15)",  color:"#f59e0b" },
      "Hardhat":   { bg:"rgba(251,191,36,0.15)",  color:"#fbbf24" },
      "Node.js":   { bg:"rgba(104,160,99,0.15)",  color:"#68a063" },
      "Stripe":    { bg:"rgba(99,91,255,0.15)",   color:"#635bff" },
      "WebSocket": { bg:"rgba(14,165,233,0.15)",  color:"#0EA5E9" },
    },
    span: 2,
  },
];

// ─── Experience ───────────────────────────────────────────────────────────────
export const experience = [
  {
    id: "techthree",
    role: "Blockchain & Backend Developer",
    company: "TechThree",
    period: "May 2023 – Present",
    current: true,
    dotColor: "var(--accent)",
    badgeColor: { bg:"rgba(232,67,147,0.1)", color:"#E84393" },
    desc: "Working on multiple confidential, high-impact Web3 and AI infrastructure projects including a decentralised GPU compute network (post-quantum crypto, EVM incentives), an AI-powered community automation platform (RAG, NLP moderation, on-chain token gating), a multi-sig asset vault (EIP-712, M-of-N approvals), and a blockchain intelligence platform (on-chain data pipelines, wallet analytics, tokenised rewards). Also shipped DApps analysing deep Uniswap V2/V3 data — wallet/token scanning, live events, synced Ethereum node, Telegram bots.",
    skills: ["Solidity","Node.js","LLM / RAG","EVM","Post-Quantum Crypto","EIP-712","PostgreSQL","AWS","Uniswap V2/V3"],
  },
  {
    id: "chainz",
    role: "Blockchain Developer",
    company: "Chainz Experts",
    period: "May 2022 – May 2023",
    current: false,
    dotColor: "var(--accent2)",
    badgeColor: { bg:"rgba(124,58,237,0.1)", color:"#7C3AED" },
    desc: "Delivered multiple NFT and smart-contract projects end-to-end. Built the TOY NFT e-commerce platform (NFT + Escrow contracts, multi-phase minting, signature verification, Stripe + WebSockets). Developed DEDW (ERC-2981 royalty), Capped Ranged (random minting, dual whitelist), and a blockchain game with an in-game NFT marketplace. Wrote, tested, deployed, and verified all contracts on Etherscan.",
    skills: ["Solidity","ERC-721A","ERC-1155","ERC-2981","Escrow","Hardhat","Node.js","Stripe","WebSocket"],
  },
  {
    id: "rbsol",
    role: "Project Manager",
    company: "RB SOL",
    period: "Oct 2021 – Apr 2022",
    current: false,
    dotColor: "var(--accent3)",
    badgeColor: { bg:"rgba(14,165,233,0.1)", color:"#0EA5E9" },
    desc: "Recruited and onboarded 16+ employees across Graphic Design, UI/UX, Mobile, and Web development. Managed multiple concurrent projects, coordinating delivery timelines and cross-functional team execution.",
    skills: ["Team Leadership","Project Management","Recruitment","Mobile & Web"],
  },
  {
    id: "datalabs",
    role: "Android Developer",
    company: "DataLabs Solutions",
    period: "Aug 2020 – Oct 2021",
    current: false,
    dotColor: "var(--accent3)",
    badgeColor: { bg:"rgba(14,165,233,0.1)", color:"#0EA5E9" },
    desc: "Worked across 13+ Android applications — debugging codebases from global developers and building new apps from scratch in Java and Kotlin.",
    skills: ["Java","Kotlin","Android","Mobile Development"],
  },
  {
    id: "education",
    role: "BS Software Engineering",
    company: "The University of Lahore",
    period: "Sep 2016 – Aug 2020",
    current: false,
    isEducation: true,
    dotColor: "#F59E0B",
    badgeColor: { bg:"rgba(245,158,11,0.12)", color:"#D97706" },
    badge: "🥉 Bronze Medalist",
    desc: "Graduated as a Bronze Medalist. Strong foundations in computer science, software engineering, algorithms, and programming.",
    skills: ["Software Engineering","Algorithms","Data Structures","OOP"],
  },
];

// ─── Contact links (used only in Contact section — About card uses personal directly) ──
export const contactLinks = [
  {
    id: "email",
    icon: "✉️",
    iconBg: "rgba(232,67,147,0.12)",
    label: "Email Me",
    value: "shakeelkhanaus@gmail.com",
    href: "mailto:shakeelkhanaus@gmail.com",
  },
  {
    id: "linkedin",
    icon: "💼",
    iconBg: "rgba(14,118,232,0.12)",
    label: "My LinkedIn",
    value: "linkedin.com/in/shakeel-khan-547a961a3",
    href: "https://www.linkedin.com/in/shakeel-khan-547a961a3/",
  },
  {
    id: "github",
    icon: "🐙",
    iconBg: "rgba(124,58,237,0.12)",
    label: "My GitHub",
    value: "github.com/Shinwari536",
    href: "https://github.com/Shinwari536",
  },
  // {
  //   id: "location",
  //   icon: "📍",
  //   iconBg: "rgba(14,165,233,0.12)",
  //   label: "Location",
  //   value: "Karachi, Pakistan · Open to Remote",
  //   href: null,
  // },
];
